import React, { FC, ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { Reminder } from "@models/reminder";
import { ActivityType } from "@models/activity-type";
import { CronUtil } from "@utils/cron.util";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import StyledChip from "@components/StyledChip";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { Mode } from "@store/homeSlice";
import ReactTimeAgo from "react-time-ago";
import { DashboardClickableTile } from "@components/Home/DashboardClickableTile";

const icons: Record<ActivityType, ReactNode> = {
  [ActivityType.Conversation]: <SmsOutlinedIcon fontSize="small" />,
  [ActivityType.GallerySession]: <InsertPhotoOutlinedIcon fontSize="small" />,
  [ActivityType.Medication]: <MedicationOutlinedIcon fontSize="small" />,
  [ActivityType.Other]: <CalendarMonthOutlinedIcon fontSize="small" />,
};

const ReminderItem: FC<{
  reminder: Reminder;
  nextDate: Date;
}> = ({ reminder, nextDate }) => {
  const mode = useSelector((state: RootState) => state.home.mode);
  const isPatient = mode === Mode.Patient;

  return (
    <DashboardClickableTile
      to={`schedule/${reminder.id}`}
      tileVariant={isPatient ? "schedulePatient" : "default"}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap="8px"
      >
        <Typography
          color="textSecondary"
          fontSize={isPatient ? "22px" : "20px"}
          fontWeight="500"
          lineHeight={isPatient ? "34px" : "30px"}
          marginBottom="6px"
        >
          {reminder.title}
        </Typography>

        <Box sx={{ color: "primary.main", display: "flex" }}>
          {icons[reminder.activityType]}
        </Box>
      </Box>

      <Typography
        fontSize={isPatient ? "16px" : "14px"}
        lineHeight="22px"
        fontWeight="400"
        color="#171A1FFF"
      >
        {reminder.description}
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginTop={isPatient ? "20px" : "17px"}
        gap="8px"
      >
        {mode === Mode.Caregiver ? (
          <StyledChip
            label={CronUtil.toHumanReadable(reminder.schedule)}
            variant="filled"
            color="primary"
          />
        ) : (
          <span />
        )}

        <Typography
          fontSize={isPatient ? "16px" : "14px"}
          lineHeight="22px"
          fontWeight={isPatient ? "500" : "400"}
          color="#171A1FFF"
        >
          <ReactTimeAgo date={nextDate} />
        </Typography>
      </Box>
    </DashboardClickableTile>
  );
};

export default ReminderItem;
